<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const MAX = 4;
const breakers = ref([]);
const isSaving = ref(false);

watch(
  () => props.inbox,
  inbox => {
    breakers.value = (inbox.ice_breakers || []).map(b => ({ ...b }));
  },
  { immediate: true }
);

const canAdd = computed(() => breakers.value.length < MAX);

const addBreaker = () => {
  breakers.value.push({
    question: '',
    payload: `IB_${Date.now()}_${breakers.value.length}`,
    answer: '',
  });
};

const removeBreaker = index => {
  breakers.value.splice(index, 1);
};

const save = async () => {
  isSaving.value = true;
  try {
    const filled = breakers.value.filter(
      b => b.question.trim() && b.answer.trim()
    );
    // Via store, não pela API direto: a aba é montada sob v-if, então sair e voltar
    // re-semeia o componente a partir da cópia do Vuex. Salvar sem atualizar o store
    // fazia as perguntas recém-salvas sumirem na volta, como se o save tivesse falhado.
    await store.dispatch('inboxes/updateInboxIceBreakers', {
      inboxId: props.inbox.id,
      iceBreakers: filled,
    });
    useAlert(t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 p-6 bg-n-solid-1">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.SUBTITLE') }}
      </p>
    </div>

    <div
      v-for="(breaker, index) in breakers"
      :key="breaker.payload"
      class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak bg-n-solid-2"
    >
      <Input
        v-model="breaker.question"
        :label="$t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.QUESTION_LABEL')"
        :placeholder="
          $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.QUESTION_PLACEHOLDER')
        "
      />
      <TextArea
        v-model="breaker.answer"
        :label="$t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.ANSWER_LABEL')"
        :placeholder="
          $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.ANSWER_PLACEHOLDER')
        "
      />
      <div class="flex justify-end">
        <NextButton
          variant="ghost"
          color="ruby"
          size="sm"
          @click="removeBreaker(index)"
        >
          {{ $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.REMOVE') }}
        </NextButton>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <NextButton
        variant="faded"
        size="sm"
        icon="i-lucide-plus"
        :disabled="!canAdd"
        @click="addBreaker"
      >
        {{ $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.ADD') }}
      </NextButton>
      <span v-if="!canAdd" class="text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.MAX_REACHED') }}
      </span>
    </div>

    <div>
      <NextButton :is-loading="isSaving" @click="save">
        {{ $t('INBOX_MGMT.INSTAGRAM_ICE_BREAKERS.SAVE') }}
      </NextButton>
    </div>
  </div>
</template>
